import React from "react";
import * as Popover from "@radix-ui/react-popover";
import { Check, ChevronDown, ChevronRight, Minus } from "lucide-react";
import clsx from "clsx";
import ClearableControl, { type ClearAction } from "./ClearableControl";

export type FilterOption = {
  id: string;
  label: string;
  /** One-click exclusive selection, shown when there is more than one option. */
  shortcutLabel?: string;
  /**
   * Header to list the option under, in first-seen order. Headers only show once the options
   * name more than one group; each checks its whole group and collapses it.
   */
  group?: string;
};

type FilterMultiSelectProps = {
  label?: string;
  ariaLabel?: string;
  value: string[];
  options: FilterOption[];
  onChange: (value: string[]) => void;
  /** Trigger text when every option is selected. */
  allLabel: string;
  /** Trigger text when nothing is selected; defaults to `allLabel`. */
  emptyLabel?: string;
  /**
   * Label for a "select all" first row that checks every option or, when everything is already
   * checked, clears them all. Omitted: no such row.
   */
  selectAllLabel?: string;
  /** The last checked option cannot be unchecked; otherwise an empty value selects nobody. */
  keepOne?: boolean;
  /** Badge variant: rounded-full trigger, to sit beside pill toggles. */
  pill?: boolean;
  triggerClassName?: string;
  hideChevron?: boolean;
  /** Renders a filter box above the options; omitted means no search. */
  searchPlaceholder?: string;
  /**
   * Collapses a partial selection to this word plus a count, instead of listing
   * the labels. For callers that already show the chosen items elsewhere.
   */
  countLabel?: string;
  /** Trigger text for multiple selected options, unless every option is selected. */
  multipleLabel?: (count: number) => string;
  /** Leading icon in the trigger, for pill variants that need one. */
  icon?: React.ReactNode;
  clearAction?: ClearAction;
  /** Prefix for the selected value, without repeating it in the option rows. */
  triggerLabelPrefix?: string;
  /** Help text shown only inside the open dropdown. */
  description?: string;
};

const TRIGGER_CLASS = {
  // Tight on purpose: four of these plus the actions share one row in the
  // composer, and any more padding tips the last pill onto a second line the
  // moment Watch adds its cadence pill.
  pill: "max-w-[240px] gap-1 rounded-full border border-outline bg-surface px-1.5 py-1 hover:bg-muted-surface",
  default: "dropdown-trigger max-w-[380px]",
};

/** Shared by the option rows, the group headers, and the select-all row above them. */
const Checkbox = ({
  checked,
  children,
}: {
  checked: boolean | "mixed";
  children?: React.ReactNode;
}) => (
  <>
    <span
      className={clsx(
        "flex h-4 w-4 shrink-0 items-center justify-center rounded border",
        checked ? "border-info bg-info text-white" : "border-outline bg-background-alt",
      )}
    >
      {checked === true && <Check className="h-3 w-3" />}
      {checked === "mixed" && <Minus className="h-3 w-3" />}
    </span>
    {children && <span className="min-w-0 truncate">{children}</span>}
  </>
);

const FilterMultiSelect = ({
  label,
  ariaLabel,
  value,
  options,
  onChange,
  allLabel,
  emptyLabel,
  selectAllLabel,
  keepOne = false,
  pill = false,
  triggerClassName,
  hideChevron = false,
  searchPlaceholder,
  countLabel,
  multipleLabel,
  icon,
  clearAction,
  triggerLabelPrefix,
  description,
}: FilterMultiSelectProps) => {
  const [search, setSearch] = React.useState("");
  const [collapsedGroups, setCollapsedGroups] = React.useState<ReadonlySet<string>>(new Set());
  const optionIds = new Set(options.map(option => option.id));
  const selected = value.filter(id => optionIds.has(id));
  const selectedSet = new Set(selected);
  const allSelected = selected.length === options.length;
  const shortcut = options.length >= 2 ? options.find(option => option.shortcutLabel) : undefined;
  const selectedLabels = options
    .filter(option => selectedSet.has(option.id))
    .map(option => option.label);
  const triggerLabel =
    multipleLabel && selectedLabels.length > 1
      ? multipleLabel(selectedLabels.length)
      : selectedLabels.join(", ");
  const groups = [...new Set(options.map(option => option.group ?? ""))];
  const grouped = groups.length > 1;
  // Search narrows what is listed, never what is selected: options checked
  // earlier stay checked while the box is filtered. A listed group's name
  // matches its whole roster.
  const query = search.trim().toLowerCase();
  const visibleOptions = options.filter(
    option =>
      option.label.toLowerCase().includes(query) ||
      (grouped && (option.group ?? "").toLowerCase().includes(query)),
  );

  const toggle = (id: string) => {
    if (selectedSet.has(id)) {
      // Keep the existing minimum selection unless this caller allows clearing it.
      if (keepOne && selected.length <= 1) {
        return;
      }
      onChange(selected.filter(selectedId => selectedId !== id));
      return;
    }
    onChange([...selected, id]);
  };

  const toggleMany = (ids: string[]) => {
    if (!ids.every(id => selectedSet.has(id))) {
      onChange([...selected, ...ids.filter(id => !selectedSet.has(id))]);
      return;
    }
    const removed = new Set(ids);
    const next = selected.filter(id => !removed.has(id));
    if (next.length > 0 || !keepOne) onChange(next);
  };

  const toggleCollapsed = (group: string) =>
    setCollapsedGroups(current => {
      const next = new Set(current);
      if (!next.delete(group)) next.add(group);
      return next;
    });

  const renderOption = (option: FilterOption, nested = false) => (
    <button
      key={option.id}
      type="button"
      aria-pressed={selectedSet.has(option.id)}
      onClick={() => toggle(option.id)}
      className={clsx("dropdown-option w-full gap-3", nested && "pl-9")}
    >
      <Checkbox checked={selectedSet.has(option.id)}>{option.label}</Checkbox>
    </button>
  );

  const renderGroup = (group: string) => {
    const members = visibleOptions.filter(option => (option.group ?? "") === group);
    if (members.length === 0) return null;
    const ids = members.map(option => option.id);
    const count = ids.filter(id => selectedSet.has(id)).length;
    const checked = count === 0 ? false : count === ids.length ? true : "mixed";
    const collapsed = collapsedGroups.has(group);
    const Chevron = collapsed ? ChevronRight : ChevronDown;
    return (
      <div key={group}>
        <div className="flex items-center">
          <button
            type="button"
            aria-pressed={checked}
            aria-label={group}
            onClick={() => toggleMany(ids)}
            className="dropdown-option shrink-0"
          >
            <Checkbox checked={checked} />
          </button>
          <button
            type="button"
            aria-expanded={!collapsed}
            onClick={() => toggleCollapsed(group)}
            className="dropdown-option min-w-0 flex-1 gap-2 pl-1 font-medium"
          >
            <span className="min-w-0 flex-1 truncate">{group}</span>
            <span className="shrink-0 text-xs font-normal text-text-dim">{ids.length}</span>
            <Chevron className="h-4 w-4 shrink-0 text-text-dim" aria-hidden="true" />
          </button>
        </div>
        {!collapsed && members.map(option => renderOption(option, true))}
      </div>
    );
  };

  const toggleAll = () => onChange(allSelected ? [] : options.map(option => option.id));

  return (
    <div
      className={clsx(
        "flex flex-col gap-1 text-text-mid",
        pill ? "min-w-0 text-xs" : "min-w-[240px] text-sm",
      )}
    >
      {label && (
        <span className="text-xs font-medium uppercase tracking-wide text-text-dim">{label}</span>
      )}
      <Popover.Root>
        <ClearableControl clearAction={clearAction}>
          <Popover.Trigger
            aria-label={ariaLabel}
            type="button"
            className={clsx(
              "flex w-full items-center justify-between text-left text-text outline-none transition",
              triggerClassName ?? TRIGGER_CLASS[pill ? "pill" : "default"],
            )}
          >
            {icon}
            <span className="min-w-0 flex-1 truncate">
              {triggerLabelPrefix}
              {selectedLabels.length === options.length
                ? allLabel
                : selectedLabels.length > 0
                  ? (countLabel ?? triggerLabel)
                  : (emptyLabel ?? allLabel)}
            </span>
            {countLabel && selectedLabels.length > 0 && selectedLabels.length < options.length && (
              <span className="shrink-0 rounded-full bg-muted-surface px-1 py-0.5 text-[10px] font-medium text-text">
                {selectedLabels.length}
              </span>
            )}
            {!clearAction && !hideChevron && (
              <ChevronDown
                className={clsx("shrink-0 text-text-dim", pill ? "h-3 w-3" : "h-4 w-4")}
              />
            )}
          </Popover.Trigger>
        </ClearableControl>
        <Popover.Portal>
          <Popover.Content
            align="end"
            sideOffset={6}
            collisionPadding={16}
            className="dropdown-menu flex w-72 max-h-[var(--radix-popover-content-available-height)] max-w-[calc(100vw-32px)] flex-col overflow-hidden"
          >
            {searchPlaceholder && (
              <div className="shrink-0 p-1 pb-2">
                <input
                  type="text"
                  value={search}
                  onChange={event => setSearch(event.target.value)}
                  placeholder={searchPlaceholder}
                  className="w-full rounded-md border border-outline bg-surface px-3 py-2 text-sm text-text outline-none placeholder:text-text-dim focus:border-info"
                />
              </div>
            )}
            {(selectAllLabel || shortcut) && (
              <div className="mb-1 flex shrink-0 items-center gap-2 border-b border-outline pb-1 pr-2">
                {selectAllLabel && (
                  <button
                    type="button"
                    onClick={toggleAll}
                    aria-pressed={allSelected}
                    className="dropdown-option min-w-0 flex-1 gap-3"
                  >
                    <Checkbox checked={allSelected}>{selectAllLabel}</Checkbox>
                  </button>
                )}
                {shortcut && (
                  <button
                    type="button"
                    onClick={() => onChange([shortcut.id])}
                    disabled={selected.length === 1 && selectedSet.has(shortcut.id)}
                    title={shortcut.label}
                    className="shrink-0 rounded-md border border-outline px-2 py-1 text-xs font-medium text-info transition hover:bg-muted-surface focus-visible:outline-info disabled:cursor-default disabled:text-text-dim"
                  >
                    {shortcut.shortcutLabel}
                  </button>
                )}
              </div>
            )}
            <div className="min-h-0 max-h-72 overflow-y-auto overscroll-contain">
              {grouped
                ? groups.map(renderGroup)
                : visibleOptions.map(option => renderOption(option))}
            </div>
            {description && (
              <p className="mt-1 shrink-0 border-t border-outline px-3 py-2 text-xs leading-4 text-text-dim">
                {description}
              </p>
            )}
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </div>
  );
};

export default FilterMultiSelect;
